<template>
  <header
    class="app-header navbar authorized-dectection"
  >
    <b-link class="navbar-brand" to="#"></b-link>
    <div v-if="!this.isClientAdmin" class="main-client-selector">
      <p class="client-selector-title">Log In as: </p>
      <ClientSelector
        id="clientSelector"
        v-model="clientSelected"
        :isFullObject="true"
        @input="handleClientChange"
        :allowEmpty="false"
      ></ClientSelector>
    </div>
    <b-navbar toggleable="md">
      <b-navbar-nav class="ml-auto">
        <b-nav-item-dropdown right>
          <template slot="text">
            <span v-if="userInfo != null">{{ userInfo.firstName }}</span>
          </template>
          <b-dropdown-item :to="profileHref">Profile</b-dropdown-item>
          <b-dropdown-item :to="changePwdHref">Change Password</b-dropdown-item>
          <b-dropdown-item
            v-if="isLoginAs && !isExternalUser"
            @click="returnToAdmin"
            >Return to Admin</b-dropdown-item
          >
          <b-dropdown-item href="#" @click="logout()">Signout</b-dropdown-item>
        </b-nav-item-dropdown>
      </b-navbar-nav>
    </b-navbar>
  </header>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { EventBus } from "../event-bus";
import consts from "../_consts";
import ClientSelector from "@/components/admin/Clients/ClientSelector";
import { EVENT_GOOGLE_ANALYTICS_INFO } from "../store/events";
import {
  ACTION_DO_LOGOUT,
  GETTER_USER_INFO
} from "@/store/modules/users/constants";
import {
  ACTION_SET_CLIENT_SELECTED,
  GETTER_CLIENT_SELECTED
} from "@/store/modules/common/constants";
import { ACTION_GET_EMPLOYER } from "@/store/modules/employers/constants";
import _ from "lodash";
import moment from "moment";
import {DateUtils} from "@aws-amplify/core";

export default {
  name: "top",
  components: {
    ClientSelector
  },
  data() {
    return {
      clientSelected: "",
      userActivityTimeout: null
    };
  },
  methods: {
    logout() {
      this.doLogout().then(() => {
        this.$router.push("/login");
        // re-set user info after logout
        this.$ga.set("userId", "N/A");
        this.$ga.set("role", "");
        this.$ga.set("employer_ids", "");
      });
    },
    returnToAdmin() {
      this.$router.push({ name: this.$adminRoute.items.home.name });
    },
    handleClientChange(client) {
      this.$ga.event("Client", "login_as", "Login as client " + client.id, 1);
      this.getEmployer({
        id: client.id,
        callback: res => {
          if (res) {
            localStorage.setItem(
              consts.LOCAL_STORE.client,
              JSON.stringify(res.data)
            );
          } else {
            localStorage.setItem(
              consts.LOCAL_STORE.client,
              JSON.stringify(client)
            );
          }
          this.$emit("input");
          this.actionSetClient(client);
          this.$router.push({
            name: this.$dashboardRoute.items.clientAdminHome.name
          });
        }
      });
    },
    activateActivityTracker() {
      window.addEventListener("mousemove", this.userActivityThrottler);
      window.addEventListener("scroll", this.userActivityThrottler);
      window.addEventListener("keydown", this.userActivityThrottler);
      window.addEventListener("resize", this.userActivityThrottler);
      window.onload = this.userActivityThrottler;
    },
    deactivateActivityTracker() {
      window.removeEventListener("mousemove", this.userActivityThrottler);
      window.removeEventListener("scroll", this.userActivityThrottler);
      window.removeEventListener("keydown", this.userActivityThrottler);
      window.removeEventListener("resize", this.userActivityThrottler);
      window.removeEventListener("onload", this.userActivityThrottler);
    },
    setActivityTimeout() {
      this.userActivityTimeout = setTimeout(() => {
        this.inactiveUserAction();
      }, consts.TIMEOUT_VARIABLES.INACTIVE_USER_TIME_THRESHOLD);
    },
    resetUserActivityTimeout() {
      clearTimeout(this.userActivityTimeout);
      this.setActivityTimeout();

      const refreshTokenAtISOString = sessionStorage.getItem(consts.LOCAL_STORE.refreshTokenAt);

      if (refreshTokenAtISOString) {
        const refreshTokenAt = moment(refreshTokenAtISOString);

        if (moment.now() > refreshTokenAt) {
          EventBus.$emit('callRefreshToken');
        }
      }
    },
    userActivityThrottler: _.throttle(function() {
      this.resetUserActivityTimeout();
    }, consts.TIMEOUT_VARIABLES.USER_ACTIVITY_THROTTLER_TIME),
    inactiveUserAction() {
      this.logout();
    },
    ...mapActions({
      getEmployer: ACTION_GET_EMPLOYER,
      doLogout: ACTION_DO_LOGOUT,
      actionSetClient: ACTION_SET_CLIENT_SELECTED
    })
  },
  computed: {
    isClientAdmin() {
      return this.userInfo.role &&
        this.userInfo.role.length > 0 &&
        this.userInfo.role[0] === 'client_admin';
    },
    userInfo() {
      return this.getUserInfo;
    },
    isClientPanel() {
      const routeHasPanel = this.$route.matched.find(
        record =>
          record &&
          record.meta.panel &&
          Object.keys(record.meta.panel).length > 0
      );
      return routeHasPanel && routeHasPanel.meta.panel === consts.panels.client;
    },
    isLoginAs() {
      return (
        !!localStorage.getItem(consts.LOCAL_STORE.client) && this.isClientPanel
      );
    },
    isExternalUser() {
      return (
        !this.userInfo ||
        !this.userInfo.roles ||
        !this.userInfo.roles.data ||
        !this.userInfo.roles.data.length ||
        this.userInfo.roles.data[0].external
      );
    },
    profileHref() {
      const route = this.isClientPanel
        ? this.$dashboardRoute.items
        : this.$adminRoute.items;
      return route.userManagement.children.profile.path;
    },
    changePwdHref() {
      const route = this.isClientPanel
        ? this.$dashboardRoute.items
        : this.$adminRoute.items;
      return route.userManagement.children.changePassword.path;
    },
    ...mapGetters({
      getUserInfo: GETTER_USER_INFO,
      getterClientSelected: GETTER_CLIENT_SELECTED
    })
  },
  created() {
    const refThis = this;
    EventBus.$on(EVENT_GOOGLE_ANALYTICS_INFO, function(event) {
      // send info to google analytics
      this.$ga.set(consts.gaCustomDimension.userId, refThis.userInfo.userId);
      this.$ga.set(consts.gaCustomDimension.userRole, refThis.userInfo.role);
      this.$ga.set(
        consts.gaCustomDimension.userName,
        refThis.userInfo.firstName + " " + refThis.userInfo.lastName
      );
      this.$ga.set(
        consts.gaCustomDimension.tabName,
        event[consts.gaCustomDimension.tabName]
      );
      if (event[consts.gaCustomDimension.searchText]) {
        this.$ga.set(
          consts.gaCustomDimension.searchText,
          event[consts.gaCustomDimension.searchText]
        );
        this.$ga.query(
          "send",
          "pageview",
          "?q=" + event[consts.gaCustomDimension.searchText]
        );
      } else {
        this.$ga.event({
          eventCategory: event.category,
          eventAction: event.action,
          eventLabel: event.label,
          eventValue: event.value
        });
      }
    });
    if (localStorage.getItem(consts.LOCAL_STORE.client)) {
      try {
        const client = JSON.parse(
          localStorage.getItem(consts.LOCAL_STORE.client) || {}
        );
        this.clientSelected = client;
        this.actionSetClient(client);
      } catch (e) {
        console.log("GET local client err: ", e);
      }
    }
  },
  mounted() {
    this.activateActivityTracker();
    this.setActivityTimeout();
  },
  beforeDestroy() {
    this.deactivateActivityTracker();
    clearTimeout(this.userActivityTimeout);
  },
  watch: {
    $route: function() {
      document.body.classList.remove("sidebar-mobile-show");
    }
  }
};

</script>



// WEBPACK FOOTER //
// src/components/Header.vue